import React, { useEffect, useMemo, useState } from 'react';
import * as Astronomy from 'astronomy-engine';
import SingularityButton from './SingularityButton';
import CosmicPulseModal from './CosmicPulseModal';
import MoonPhasePhoto from './MoonPhasePhoto';
import { getMoonPhase as getCurrentMoonPhase } from '../../data/sacredCalendar';

// Zodiac icon images
const ZODIAC_IMAGES: Record<string, string> = {
  Aries: `${process.env.PUBLIC_URL}/zodiac/aries.png`,
  Taurus: `${process.env.PUBLIC_URL}/zodiac/taurus.png`,
  Gemini: `${process.env.PUBLIC_URL}/zodiac/gemini.png`,
  Cancer: `${process.env.PUBLIC_URL}/zodiac/cancer.png`,
  Leo: `${process.env.PUBLIC_URL}/zodiac/leo.png`,
  Virgo: `${process.env.PUBLIC_URL}/zodiac/virgo.png`,
  Libra: `${process.env.PUBLIC_URL}/zodiac/libra.png`,
  Scorpio: `${process.env.PUBLIC_URL}/zodiac/scorpio.png`,
  Sagittarius: `${process.env.PUBLIC_URL}/zodiac/sagittarius.png`,
  Capricorn: `${process.env.PUBLIC_URL}/zodiac/capricorn.png`,
  Aquarius: `${process.env.PUBLIC_URL}/zodiac/aquarius.png`,
  Pisces: `${process.env.PUBLIC_URL}/zodiac/pisces.png`,
};

const ZODIAC_DETAILS: Record<string, string> = {
  Aries: `${process.env.PUBLIC_URL}/zodiac/aries-detail.jpg`,
  Taurus: `${process.env.PUBLIC_URL}/zodiac/taurus-detail.jpg`,
  Gemini: `${process.env.PUBLIC_URL}/zodiac/gemini-detail.jpg`,
  Cancer: `${process.env.PUBLIC_URL}/zodiac/cancer-detail.jpg`,
  Leo: `${process.env.PUBLIC_URL}/zodiac/leo-detail.jpg`,
  Virgo: `${process.env.PUBLIC_URL}/zodiac/virgo-detail.jpg`,
  Libra: `${process.env.PUBLIC_URL}/zodiac/libra-detail.jpg`,
  Scorpio: `${process.env.PUBLIC_URL}/zodiac/scorpio-detail.jpg`,
  Sagittarius: `${process.env.PUBLIC_URL}/zodiac/sagittarius-detail.jpg`,
  Capricorn: `${process.env.PUBLIC_URL}/zodiac/capricorn-detail.jpg`,
  Aquarius: `${process.env.PUBLIC_URL}/zodiac/aquarius-detail.jpg`,
  Pisces: `${process.env.PUBLIC_URL}/zodiac/pisces-detail.jpg`,
};

interface ZodiacWheelProps {
  size?: number; // default 300
}

interface ZodiacSignData {
  name: string;
  symbol: string;
  startDay: number;
  dateRange: string;
  element: string;
  modality: string;
  rulingPlanet: string;
  notableStars: string;
  description: string;
  strengths: string;
  shadow: string;
}

const ZodiacWheel: React.FC<ZodiacWheelProps> = ({ size = 300 }) => {
  const [selectedSign, setSelectedSign] = useState<ZodiacSignData | null>(null);
  const [selectedPlanet, setSelectedPlanet] = useState<any | null>(null);
  const [showCosmicPulse, setShowCosmicPulse] = useState(false);

  // Lock body scroll when any modal is open (iOS-safe)
  useEffect(() => {
    if (selectedSign || selectedPlanet) {
      const scrollY = window.scrollY;
      const root = document.getElementById('root');
      if (root) {
        root.style.position = 'fixed';
        root.style.top = `-${scrollY}px`;
        root.style.left = '0';
        root.style.right = '0';
        root.style.overflow = 'hidden';
      }
      return () => {
        if (root) {
          root.style.position = '';
          root.style.top = '';
          root.style.left = '';
          root.style.right = '';
          root.style.overflow = '';
        }
        window.scrollTo(0, scrollY);
      };
    }
  }, [selectedSign, selectedPlanet]);

  const zodiacSigns: ZodiacSignData[] = [
    { name: 'Aries', symbol: '♈', startDay: 80, dateRange: 'March 21 - April 19', element: 'Fire', modality: 'Cardinal', rulingPlanet: 'Mars', notableStars: 'Hamal, Sheratan, Mesarthim', description: 'The pioneer and warrior of the zodiac. Aries carries the spark of creation itself — the raw, unfiltered courage to begin. Ruled by Mars, this sign does not wait for permission or the perfect moment; it acts on instinct and trusts the fire within. Aries teaches us that initiation is sacred, that the first step holds more power than a thousand plans. When Aries energy is alight, stagnation breaks and new worlds become possible.', strengths: 'Bold, Independent, Energetic, Direct, Courageous', shadow: 'Impatience, Aggression' },
    { name: 'Taurus', symbol: '♉', startDay: 110, dateRange: 'April 20 - May 20', element: 'Earth', modality: 'Fixed', rulingPlanet: 'Venus', notableStars: 'Aldebaran', description: 'The builder and keeper of the earthly temple. Taurus knows that the sacred is not only found in the heavens — it lives in soil, in silk, in the slow accumulation of devotion made tangible. Ruled by Venus, this sign finds the divine through the senses: the weight of a handmade object, the taste of a meal prepared with love, the patience to let something beautiful grow. Taurus teaches us that commitment is not confinement — it is the ground from which everything rises.', strengths: 'Patient, Reliable, Devoted, Sensual, Grounded', shadow: 'Stubbornness, Possessiveness' },
    { name: 'Gemini', symbol: '♊', startDay: 141, dateRange: 'May 21 - June 20', element: 'Air', modality: 'Mutable', rulingPlanet: 'Mercury', notableStars: 'Castor, Pollux', description: 'The messenger and weaver of worlds. Gemini exists in the liminal space between knowing and wondering, always gathering, always translating. Ruled by Mercury, this sign treats every conversation as a portal and every idea as a thread waiting to be pulled. Gemini reminds us that identity is not a fixed point but a constellation — we contain multitudes, and that is not confusion but brilliance. The gift of Gemini is making the complex feel effortless and the ordinary feel fascinating.', strengths: 'Curious, Adaptable, Witty, Communicative, Versatile', shadow: 'Restlessness, Inconsistency' },
    { name: 'Cancer', symbol: '♋', startDay: 172, dateRange: 'June 21 - July 22', element: 'Water', modality: 'Cardinal', rulingPlanet: 'Moon', notableStars: 'Beehive Cluster (M44)', description: 'The nurturer and guardian of the inner kingdom. Cancer carries the wisdom of the tides — the understanding that all creation begins in darkness and safety before it can emerge into light. Ruled by the Moon, this sign feels what the world cannot yet name, protects what is most vulnerable, and remembers what others forget. Cancer teaches us that home is not a place but a practice: the radical act of creating sanctuary in a world that offers none. Their strength is not softness — it is the ferocity behind the care.', strengths: 'Intuitive, Protective, Empathic, Loyal, Nurturing', shadow: 'Moodiness, Clinging' },
    { name: 'Leo', symbol: '♌', startDay: 203, dateRange: 'July 23 - August 22', element: 'Fire', modality: 'Fixed', rulingPlanet: 'Sun', notableStars: 'Regulus, Denebola', description: 'The sovereign and radiant heart of the zodiac. Leo does not perform for approval — Leo performs because the act of creation is the most sacred prayer. Ruled by the Sun, this sign carries the archetype of the inner king or queen: the one who rules by generosity, not domination, and whose light illuminates everyone in its orbit. Leo teaches us that confidence is not arrogance but alignment — when you stand fully in your truth, you give others permission to do the same. The world needs what only you can give.', strengths: 'Confident, Generous, Creative, Magnetic, Courageous', shadow: 'Pride, Need for Recognition' },
    { name: 'Virgo', symbol: '♍', startDay: 235, dateRange: 'August 23 - September 22', element: 'Earth', modality: 'Mutable', rulingPlanet: 'Mercury', notableStars: 'Spica', description: 'The healer and sacred craftsman. Virgo sees the world not as it is but as it could be — and then quietly, methodically, closes the gap. Ruled by Mercury, this sign channels analytical brilliance into acts of service that feel like devotion. Where others see chaos, Virgo sees the pattern waiting to emerge. This sign teaches us that attention is the rarest form of love, that small acts done with precision carry more weight than grand gestures done carelessly, and that healing often begins with simply noticing what has been overlooked.', strengths: 'Analytical, Devoted, Practical, Grounded, Meticulous', shadow: 'Perfectionism, Self-Criticism' },
    { name: 'Libra', symbol: '♎', startDay: 266, dateRange: 'September 23 - October 22', element: 'Air', modality: 'Cardinal', rulingPlanet: 'Venus', notableStars: 'Zubenelgenubi, Zubeneschamali', description: 'The diplomat and artist of relationship. Libra understands something most miss: that beauty is not decoration but a moral force — that environments, conversations, and connections shaped by grace change the people within them. Ruled by Venus, this sign seeks not shallow harmony but the deep equilibrium that comes from truly hearing both sides. Libra teaches us that fairness is an act of imagination, that choosing peace is not weakness, and that the space between two people can be the most sacred place of all.', strengths: 'Graceful, Fair, Charming, Cooperative, Diplomatic', shadow: 'Indecision, People-Pleasing' },
    { name: 'Scorpio', symbol: '♏', startDay: 297, dateRange: 'October 23 - November 21', element: 'Water', modality: 'Fixed', rulingPlanet: 'Mars & Pluto', notableStars: 'Antares', description: 'The alchemist who knows that gold is forged only in the deepest fire. Scorpio does not fear the dark — it knows the dark is where truth lives, unadorned and undeniable. Ruled by Mars and Pluto, this sign carries both the warrior\'s primal drive and the underworld\'s transformative power: raw courage to act and the wisdom to let old selves die so truer ones can emerge. Mars gives Scorpio its fierce will and sexual intensity; Pluto grants the archetype of death and rebirth. Scorpio teaches us that transformation is not optional, that vulnerability mistaken for weakness is actually the gateway to real power, and that what we refuse to face will face us eventually. To know Scorpio is to know that nothing truly dies — it only changes form.', strengths: 'Powerful, Perceptive, Brave, Transformative, Loyal', shadow: 'Obsession, Vindictiveness' },
    { name: 'Sagittarius', symbol: '♐', startDay: 326, dateRange: 'November 22 - December 21', element: 'Fire', modality: 'Mutable', rulingPlanet: 'Jupiter', notableStars: 'Kaus Australis', description: 'The explorer and philosopher-king of the zodiac. Sagittarius knows that the longest journey is not across continents but across the boundaries of the mind. Ruled by Jupiter, this sign is driven by an insatiable hunger for meaning — not answers, but better questions. Every horizon reached reveals another, and that is not frustration but ecstasy. Sagittarius teaches us that freedom is not the absence of commitment but the presence of purpose, that laughter is a form of wisdom, and that the seeker who never arrives has understood the journey perfectly.', strengths: 'Adventurous, Optimistic, Wise, Free, Philosophical', shadow: 'Excess, Restlessness' },
    { name: 'Capricorn', symbol: '♑', startDay: 355, dateRange: 'December 22 - January 19', element: 'Earth', modality: 'Cardinal', rulingPlanet: 'Saturn', notableStars: 'Algedi, Dabih', description: 'The architect who builds what outlasts the builder. Capricorn carries the weight of time like a crown — not as burden but as responsibility. Ruled by Saturn, this sign understands what most refuse to accept: that mastery requires sacrifice, that every great structure was built one unseen stone at a time, and that the mountain does not care about your feelings. Capricorn teaches us that ambition rooted in integrity is not ego but destiny fulfilled, that discipline is the most loving thing you can offer your future self, and that the summit matters less than who you became on the climb.', strengths: 'Disciplined, Ambitious, Wise, Resilient, Strategic', shadow: 'Rigidity, Workaholism' },
    { name: 'Aquarius', symbol: '♒', startDay: 20, dateRange: 'January 20 - February 18', element: 'Air', modality: 'Fixed', rulingPlanet: 'Saturn & Uranus', notableStars: 'Sadalsuud', description: 'The visionary who carries tomorrow in their bones. Aquarius does not rebel for the sake of rebellion — it disrupts because it has already seen what lies on the other side of the old order, and waiting feels like betrayal. Ruled by Saturn and Uranus, this sign holds both the architect\'s discipline and the revolutionary\'s lightning: Saturn builds the structures that endure, while Uranus shatters what no longer serves. This tension creates the genius of Aquarius — grounded innovation, principled rebellion, and the capacity to care for humanity\'s future while struggling to hold one person close. Aquarius teaches us that progress requires disruption, that being misunderstood is often the price of being ahead of your time, and that the future belongs to those brave enough to imagine it differently.', strengths: 'Innovative, Humanitarian, Independent, Visionary, Unconventional', shadow: 'Detachment, Emotional Distance' },
    { name: 'Pisces', symbol: '♓', startDay: 50, dateRange: 'February 19 - March 20', element: 'Water', modality: 'Mutable', rulingPlanet: 'Jupiter & Neptune', notableStars: 'Eta Piscium', description: 'The mystic who walks between worlds with eyes open and heart unguarded. Pisces is where the zodiac completes its great cycle — and begins again — dissolving every boundary the other signs worked so hard to build. Ruled by Jupiter and Neptune, this sign holds both the philosopher\'s faith in meaning and the mystic\'s surrender to mystery: Jupiter expands Pisces toward wisdom and hope, while Neptune dissolves all edges into infinite compassion. Together they create a sign that channels the divine through art, dreams, and an imagination that refuses to accept the visible world as the only one. Pisces teaches us that surrender is not defeat, that the dreamer who acts is the most powerful force on earth, and that empathy — true, costly, world-breaking empathy — is not weakness but the final and most difficult strength.', strengths: 'Intuitive, Compassionate, Artistic, Transcendent, Imaginative', shadow: 'Escapism, Victimhood' },
  ];

  const { currentSign, currentAngle, dayOfYear } = useMemo(() => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const diff = now.getTime() - startOfYear.getTime();
    const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;

    // Find current sign
    let currentSign = zodiacSigns[0];
    for (let i = 0; i < zodiacSigns.length; i++) {
      const sign = zodiacSigns[i];
      const nextSign = zodiacSigns[(i + 1) % zodiacSigns.length];
      
      // Handle wrap around for Capricorn -> Aquarius -> Pisces -> Aries
      if (sign.startDay > nextSign.startDay) {
        // Year-end wrap
        if (dayOfYear >= sign.startDay || dayOfYear < nextSign.startDay) {
          currentSign = sign;
          break;
        }
      } else {
        if (dayOfYear >= sign.startDay && dayOfYear < nextSign.startDay) {
          currentSign = sign;
          break;
        }
      }
    }

    // Calculate angle
    // Aries starts at March 21 (day 80) = 270° in SVG coordinates (9 o'clock)
    // Days since Aries start
    let daysSinceAries = dayOfYear - 80;
    if (daysSinceAries < 0) daysSinceAries += 365;
    
    // Convert to zodiac degrees (0-360, where 0 = Aries start)
    const zodiacDegrees = (daysSinceAries / 365) * 360;
    
    // Convert to SVG degrees (add 270° to make Aries at 9 o'clock)
    const svgDegrees = (zodiacDegrees + 270) % 360;

    return { currentSign, currentAngle: svgDegrees, dayOfYear };
  }, []);

  const center = size / 2;
  const radius = (size / 2) - 20;
  const innerRadius = radius - 30;

  // Create SVG path for each zodiac segment
  const createSegmentPath = (startAngle: number, endAngle: number) => {
    const start = polarToCartesian(center, center, radius, startAngle);
    const end = polarToCartesian(center, center, radius, endAngle);
    const innerStart = polarToCartesian(center, center, innerRadius, endAngle);
    const innerEnd = polarToCartesian(center, center, innerRadius, startAngle);

    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} L ${innerStart.x} ${innerStart.y} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerEnd.x} ${innerEnd.y} Z`;
  };

  const polarToCartesian = (cx: number, cy: number, r: number, angle: number) => {
    const rad = (angle - 90) * Math.PI / 180;
    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad),
    };
  };

  // Convert ecliptic longitude to zodiac sign
  const longitudeToSign = (lon: number): string => {
    const signs = [
      'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
      'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
    ];
    const index = Math.floor(lon / 30) % 12;
    return signs[index];
  };

  // Calculate position for zodiac symbol text
  const getSymbolPosition = (index: number) => {
    // Start at 270° (9 o'clock) and go clockwise
    const startAngle = 270 + (index * 30);
    const midAngle = startAngle + 15;
    const symbolRadius = (radius + innerRadius) / 2;
    return polarToCartesian(center, center, symbolRadius, midAngle);
  };

  // Calculate planetary positions using real ephemeris data
  const planetaryPositions = useMemo(() => {
    const date = new Date();
    
    const planets = [
      { name: 'Sun', body: Astronomy.Body.Sun, symbol: '☉', color: '#e67e22',
        image: `${process.env.PUBLIC_URL}/sun-modal.jpg`,
        mystical: 'The Sun is your essential self — the radiant core of who you are when all masks fall away. It governs vitality, willpower, creative life force, and conscious identity. This is your hero\'s journey, your divine spark, the light that never dims. Where the Sun shines in your chart, you are called to lead, to create, and to step into full authentic power. The Sun shows how you shine, what feeds your soul, and the unique way your spirit expresses itself in this lifetime. It is both your purpose and your presence — the sacred "I AM" that defines your place in the cosmos.' },
      { name: 'Moon', body: Astronomy.Body.Moon, symbol: '☽', color: '#c0c0c0',
        image: `${process.env.PUBLIC_URL}/planets/moon.jpg`,
        mystical: 'The Moon is your emotional body — the hidden landscape of needs, instincts, memories, and the subconscious patterns that shape you beneath conscious awareness. She governs intuition, nurturing, receptivity, and the rhythms of your inner world. The Moon is your primal self, your childhood conditioning, and the sanctuary you retreat to when the world becomes too much. Where the Moon resides in your chart, you seek comfort, safety, and belonging. She rules cycles, phases, and the eternal ebb and flow of feeling. The Moon is your mother, your home, and the quiet voice that knows you before words can name it.' },
      { name: 'Mercury', body: Astronomy.Body.Mercury, symbol: '☿', color: '#f1c40f',
        image: `${process.env.PUBLIC_URL}/planets/mercury.jpg`,
        mystical: 'Mercury is the messenger — how you think, speak, learn, and weave meaning from the chaos of experience. It governs language, logic, commerce, writing, and the bridges between minds. Mercury is your mental fingerprint, the way ideas move through you, and the stories you tell to make sense of life. Where Mercury falls in your chart reveals how you process information, how you connect the dots, and how you teach what you know. This is the voice of your curiosity, the spark of wit, and the sacred art of translation between worlds.' },
      { name: 'Venus', body: Astronomy.Body.Venus, symbol: '♀', color: '#2ecc71',
        image: `${process.env.PUBLIC_URL}/planets/venus.jpg`,
        mystical: 'Venus is the principle of love, beauty, desire, and the magnetic pull of attraction. She governs art, harmony, pleasure, values, and the aesthetics of your soul. Venus is what you find beautiful, who you attract, how you love, and what you deem worthy. She rules sensuality, romance, and the delicate dance of connection. Where Venus lives in your chart, you seek sweetness, create beauty, and align with grace. This is the goddess within — your capacity for pleasure, your relationship to worth, and the art you bring into being simply by existing.' },
      { name: 'Mars', body: Astronomy.Body.Mars, symbol: '♂', color: '#e74c3c',
        image: `${process.env.PUBLIC_URL}/planets/mars.jpg`,
        mystical: 'Mars is your warrior spirit — raw drive, courage, desire, anger, and the will to act in the face of resistance. It governs ambition, assertion, sexuality, competition, and the fire that propels you forward. Mars is your inner warrior, your primal instinct to fight for what you want and defend what you love. Where Mars burns in your chart, you are fierce, alive, and uncompromising. This is your passion, your rage, your libido, and the blade you sharpen when softness no longer serves. Mars does not ask permission — it acts.' },
      { name: 'Jupiter', body: Astronomy.Body.Jupiter, symbol: '♃', color: '#3498db',
        image: `${process.env.PUBLIC_URL}/planets/jupiter.jpg`,
        mystical: 'Jupiter is the great benefic — expansion, abundance, optimism, wisdom, and the faith that life is fundamentally generous. It governs higher learning, philosophy, travel, spirituality, and the quest for meaning. Jupiter is your capacity to grow beyond limits, to believe in the impossible, and to trust that the universe conspires in your favor. Where Jupiter expands in your chart, fortune follows, horizons open, and your worldview deepens. This is the teacher, the adventurer, the seeker of truth — and the promise that there is always more.' },
      { name: 'Saturn', body: Astronomy.Body.Saturn, symbol: '♄', color: '#8e44ad',
        image: `${process.env.PUBLIC_URL}/planets/saturn.jpg`,
        mystical: 'Saturn is the great teacher — discipline, structure, time, karma, and the architecture of maturity earned through trial. It governs boundaries, responsibility, authority, and the wisdom that comes only through struggle and endurance. Saturn is the taskmaster, the elder, the voice that says "not yet" until you are ready. Where Saturn stands in your chart, you face your greatest tests, confront your deepest fears, and build your most enduring strength. This is the path of mastery — slow, unforgiving, and ultimately liberating. Saturn makes you worthy.' },
      { name: 'Uranus', body: Astronomy.Body.Uranus, symbol: '♅', color: '#1abc9c',
        image: `${process.env.PUBLIC_URL}/planets/uranus.jpg`,
        mystical: 'Uranus is the awakener — revolution, genius, liberation, sudden change, and the electric shock of truth that shatters all illusion. It governs innovation, rebellion, eccentricity, and the impulse to break every rule that no longer serves evolution. Uranus is the lightning strike, the breakthrough, the moment everything shifts. Where Uranus disrupts in your chart, you are called to break free, to think beyond convention, and to become radically yourself. This is the future arriving now — unpredictable, exhilarating, and utterly uncompromising.' },
      { name: 'Neptune', body: Astronomy.Body.Neptune, symbol: '♆', color: '#e91e8f',
        image: `${process.env.PUBLIC_URL}/planets/neptune.jpg`,
        mystical: 'Neptune is the mystic — dreams, illusion, transcendence, compassion, and the dissolution of all boundaries between self and source. It governs spirituality, imagination, artistry, and the longing to merge with something greater than the ego. Neptune is the veil between worlds, the place where reality softens and the sacred seeps through. Where Neptune shimmers in your chart, you are permeable, intuitive, and capable of touching the divine. This is your connection to the ineffable — and the risk of losing yourself entirely in the dreaming.' },
      { name: 'Pluto', body: Astronomy.Body.Pluto, symbol: '♇', color: '#4a4a5a',
        image: `${process.env.PUBLIC_URL}/planets/pluto.jpg`,
        mystical: 'Pluto is the alchemist — death, rebirth, power, obsession, and the transformation that comes only through total surrender and descent into the underworld. It governs the unconscious, regeneration, and the cycles of destruction that precede all creation. Pluto is the shadow, the taboo, the place where you meet what cannot be controlled. Where Pluto resides in your chart, you are stripped down, rebuilt, and forged into something unbreakable. This is the phoenix path — you must die to what you were to become what you are meant to be.' },
    ];

    const rawPositions = planets.map(planet => {
      let lon: number;
      if (planet.body === Astronomy.Body.Moon) {
        lon = Astronomy.EclipticGeoMoon(date).lon;
      } else if (planet.body === Astronomy.Body.Sun) {
        lon = Astronomy.SunPosition(date).elon;
      } else {
        const equatorial = Astronomy.Equator(planet.body, date, new Astronomy.Observer(0, 0, 0), true, true);
        const ecl = Astronomy.Ecliptic(equatorial.vec);
        lon = ecl.elon;
      }
      const svgAngle = (lon + 270) % 360;
      return { ...planet, longitude: lon, svgAngle, radiusOffset: 24 };
    });

    // Spread conjunct planets so they don't overlap
    // Sort by angle, then nudge overlapping ones apart angularly
    const sorted = [...rawPositions].sort((a, b) => a.svgAngle - b.svgAngle);
    const minSep = 8; // minimum degrees between symbols
    
    // Multiple passes to resolve clusters
    for (let pass = 0; pass < 5; pass++) {
      for (let i = 0; i < sorted.length; i++) {
        const j = (i + 1) % sorted.length;
        let diff = sorted[j].svgAngle - sorted[i].svgAngle;
        if (diff < 0) diff += 360;
        if (diff < minSep) {
          const nudge = (minSep - diff) / 2;
          sorted[i].svgAngle = (sorted[i].svgAngle - nudge + 360) % 360;
          sorted[j].svgAngle = (sorted[j].svgAngle + nudge) % 360;
        }
      }
    }

    return sorted;
  }, []);

  // Get element color
  const getElementColor = (element: string): string => {
    switch (element) {
      case 'Fire': return '#e67e22';
      case 'Earth': return '#27ae60';
      case 'Air': return '#3498db';
      case 'Water': return '#9b59b6';
      default: return '#c9a84c';
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      gap: '12px',
      fontFamily: 'Cinzel, serif'
    }}>
      {/* Zodiac wheel with centered singularity */}
      <div style={{ position: 'relative', width: size, height: size }}>
      <svg 
        width={size + 48} 
        height={size + 48} 
        viewBox={`-24 -24 ${size + 48} ${size + 48}`}
        style={{ 
          marginLeft: '-24px',
          marginTop: '-24px',
          filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))',
          overflow: 'visible'
        }}
      >
        {/* Background circle */}
        <circle cx={center} cy={center} r={radius} fill="#0a0a1a" />
        {/* Outer circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#c9a84c"
          strokeWidth="1"
        />
        
        {/* Inner circle */}
        <circle
          cx={center}
          cy={center}
          r={innerRadius}
          fill="none"
          stroke="#c9a84c"
          strokeWidth="0.5"
          opacity="0.3"
        />

        {/* Seasonal circle overlay */}
        <image
          href={`${process.env.PUBLIC_URL}/zodiac-seasonal-overlay.png`}
          x={center - innerRadius}
          y={center - innerRadius}
          width={innerRadius * 2}
          height={innerRadius * 2}
          style={{ pointerEvents: 'none' }}
        />

        {/* Segment dividers and gradients */}
        {zodiacSigns.map((sign, index) => {
          const startAngle = 270 + (index * 30);
          const endAngle = startAngle + 30;
          const dividerEnd = polarToCartesian(center, center, radius, endAngle);
          const dividerStart = polarToCartesian(center, center, innerRadius, endAngle);
          
          // Gradient based on position (top = brighter, bottom = darker)
          const normalizedAngle = (startAngle + 15) % 360;
          const brightness = normalizedAngle > 90 && normalizedAngle < 270 
            ? 0.15  // Bottom half (darker)
            : 0.25; // Top half (brighter)
          
          return (
            <g key={sign.name}>
              {/* Segment background */}
              <path
                d={createSegmentPath(startAngle, endAngle)}
                fill={`rgba(201, 168, 76, ${brightness})`}
                opacity="0.1"
                onClick={() => setSelectedSign(sign)}
                style={{ cursor: 'pointer' }}
              />
              
              {/* Segment divider */}
              <line
                x1={dividerStart.x}
                y1={dividerStart.y}
                x2={dividerEnd.x}
                y2={dividerEnd.y}
                stroke="#c9a84c"
                strokeWidth="0.5"
                opacity="0.2"
              />
            </g>
          );
        })}

        {/* Zodiac symbols */}
        {zodiacSigns.map((sign, index) => {
          const pos = getSymbolPosition(index);
          const isCurrentSign = sign.name === currentSign.name;
          const imgSize = 24;
          
          return (
            <g key={sign.name}>
              {ZODIAC_IMAGES[sign.name] ? (
                <image
                  href={ZODIAC_IMAGES[sign.name]}
                  x={pos.x - imgSize / 2}
                  y={pos.y - imgSize / 2}
                  width={imgSize}
                  height={imgSize}
                  
                  style={{ cursor: 'pointer', opacity: 1 }}
                  onClick={() => setSelectedSign(sign)}
                />
              ) : (
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="18"
                  fill='#c9a84c'
                  fontWeight='normal'
                  style={{ userSelect: 'none', cursor: 'pointer' }}
                  onClick={() => setSelectedSign(sign)}
                >
                  {sign.symbol}
                </text>
              )}
            </g>
          );
        })}

        {/* Planetary symbols tracking current positions */}
        {planetaryPositions.map((planet) => {
          const pos = polarToCartesian(center, center, radius + planet.radiusOffset, planet.svgAngle);
          return (
            <text
              key={planet.name}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              fill={planet.color}
              fontSize={planet.name === 'Sun' ? 27 : 20}
              fontWeight={(planet.name === 'Mars' || planet.name === 'Venus') ? 'bold' : 'normal'}
              filter="url(#glow)"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedPlanet(planet)}
            >
              {planet.symbol}
            </text>
          );
        })}

        {/* Glow filter definition */}
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>

      {/* Singularity button in exact center of wheel */}
      <div style={{
        position: 'absolute',
        top: '51%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
      }}>
        <SingularityButton onClick={() => setShowCosmicPulse(true)} size={80} />
      </div>
      </div>

      {/* Current sign label */}
      <div style={{
        color: '#c9a84c',
        fontSize: '16px',
        fontWeight: '600',
        letterSpacing: '0.5px',
        fontFamily: 'Cinzel, serif'
      }}>
        {currentSign.name} Season
      </div>
      <div style={{
        fontSize: '12px',
        color: 'rgba(255,255,255,0.65)',
        marginTop: '4px',
      }}>
        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase()}
      </div>

      {/* Modal */}
      {selectedSign && (
        <div
          onClick={() => setSelectedSign(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            paddingTop: 'calc(env(safe-area-inset-top, 20px) + 72px)',
            paddingBottom: '16px',
            animation: 'fadeIn 0.2s ease-out',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes scaleIn {
              from { transform: scale(0.9); opacity: 0; }
              to { transform: scale(1); opacity: 1; }
            }
          `}</style>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0e0e14',
              border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: '18px',
              padding: '32px 24px 24px',
              maxWidth: '500px',
              width: '100%',
              position: 'relative',
              animation: 'scaleIn 0.3s ease-out',
              marginTop: '0px',
              marginBottom: '40px',
            }}
          >
            {/* Sign name + symbol */}
            <div style={{
              fontFamily: 'Cinzel, serif',
              fontSize: '32px',
              color: '#c9a84c',
              marginBottom: '8px',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
            }}>
              {ZODIAC_IMAGES[selectedSign.name] ? (
                <img src={ZODIAC_IMAGES[selectedSign.name]} alt={selectedSign.name} style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', }} />
              ) : (
                <span>{selectedSign.symbol}</span>
              )}
              <span>{selectedSign.name}</span>
            </div>

            {/* Detailed artwork image */}
            {ZODIAC_DETAILS[selectedSign.name] && (
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <img src={ZODIAC_DETAILS[selectedSign.name]} alt={selectedSign.name} style={{ width: '100%', maxWidth: '320px', aspectRatio: '1/1', borderRadius: '12px', objectFit: 'contain', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)', display: 'block', margin: '0 auto' }} />
              </div>
            )}

            {/* Date range */}
            <div style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.5)',
              textAlign: 'center',
              marginBottom: '20px',
              fontFamily: 'Inter, sans-serif',
            }}>
              {selectedSign.dateRange}
            </div>

            {/* Element badge */}
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              marginBottom: '24px',
            }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: '12px',
                background: `${getElementColor(selectedSign.element)}20`,
                color: getElementColor(selectedSign.element),
                fontSize: '12px',
                fontWeight: '600',
                fontFamily: 'Inter, sans-serif',
              }}>
                {selectedSign.element}
              </span>
              <span style={{
                padding: '4px 12px',
                borderRadius: '12px',
                background: 'rgba(201,168,76,0.1)',
                color: 'rgba(201,168,76,0.7)',
                fontSize: '12px',
                fontWeight: '600',
                fontFamily: 'Inter, sans-serif',
              }}>
                {selectedSign.modality}
              </span>
            </div>

            {/* Ruling planet */}
            <div style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.4)',
              marginBottom: '16px',
              fontFamily: 'Inter, sans-serif',
              textAlign: 'center',
            }}>
              Ruled by <span style={{ color: '#c9a84c', fontWeight: '600' }}>{selectedSign.rulingPlanet}</span>
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', marginBottom: '10px', textAlign: 'center' }}>
              Notable stars <span style={{ color: 'rgba(255,255,255,0.65)' }}>{selectedSign.notableStars}</span>
            </div>

            {/* Description */}
            <div style={{
              fontSize: '15px',
              color: 'rgba(255,255,255,0.7)',
              lineHeight: '1.6',
              marginBottom: '20px',
              fontFamily: 'Inter, sans-serif',
              fontStyle: 'italic',
              textAlign: 'center',
            }}>
              {selectedSign.description}
            </div>

            {/* Strengths */}
            <div style={{
              marginBottom: '16px',
            }}>
              <div style={{
                fontSize: '12px',
                color: 'rgba(201,168,76,0.6)',
                marginBottom: '8px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}>
                Strengths
              </div>
              <div style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.6)',
                fontFamily: 'Inter, sans-serif',
              }}>
                {selectedSign.strengths}
              </div>
            </div>

            {/* Shadow */}
            <div>
              <div style={{
                fontSize: '12px',
                color: 'rgba(201,168,76,0.6)',
                marginBottom: '8px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}>
                Shadow
              </div>
              <div style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.6)',
                fontFamily: 'Inter, sans-serif',
              }}>
                {selectedSign.shadow}
              </div>
            </div>

            {/* Close button at bottom */}
            <button
              onClick={() => setSelectedSign(null)}
              style={{
                width: '100%',
                marginTop: '20px',
                padding: '12px',
                background: 'rgba(201,168,76,0.1)',
                border: '1px solid rgba(201,168,76,0.25)',
                borderRadius: '12px',
                color: '#c9a84c',
                fontFamily: 'Inter, sans-serif',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(201,168,76,0.18)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(201,168,76,0.1)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Planet Modal */}
      {selectedPlanet && (
        <div
          onClick={() => setSelectedPlanet(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            paddingTop: 'calc(env(safe-area-inset-top, 20px) + 72px)',
            paddingBottom: '60px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(145deg, #0e0e14, #1a1a2e)',
              border: `1px solid ${selectedPlanet.color}40`,
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '380px',
              width: '100%',
              flexShrink: 0,
              boxShadow: `0 0 40px ${selectedPlanet.color}20`,
            }}
          >
            {/* Planet image */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              {selectedPlanet.name === 'Moon' ? (() => {
                const currentPhase = getCurrentMoonPhase();
                return (
                  <MoonPhasePhoto
                    dayInCycle={currentPhase.dayInCycle}
                    phaseName={currentPhase.phaseName}
                    size={200}
                  />
                );
              })() : (
                <img
                  src={selectedPlanet.image}
                  alt={selectedPlanet.name}
                  style={{
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `2px solid ${selectedPlanet.color}60`,
                    boxShadow: `0 0 20px ${selectedPlanet.color}30`,
                  }}
                />
              )}
            </div>

            {/* Planet name and symbol */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '28px', marginRight: '8px' }}>{selectedPlanet.symbol}</span>
              <span style={{
                fontSize: '24px',
                color: selectedPlanet.color,
                fontFamily: 'Cinzel, serif',
                fontWeight: '700',
              }}>
                {selectedPlanet.name}
              </span>
            </div>

            {/* Current position */}
            <div style={{
              textAlign: 'center',
              fontSize: '13px',
              color: 'rgba(201,168,76,0.7)',
              marginBottom: '20px',
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '1px',
              textTransform: 'uppercase',
            }}>
              Currently in {longitudeToSign(selectedPlanet.longitude)} ({selectedPlanet.longitude.toFixed(1)}°)
            </div>

            {/* Mystical description */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.8)',
                fontFamily: 'Inter, sans-serif',
                lineHeight: '1.7',
              }}>
                {selectedPlanet.mystical}
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={() => setSelectedPlanet(null)}
              style={{
                width: '100%',
                padding: '12px',
                background: `${selectedPlanet.color}15`,
                border: `1px solid ${selectedPlanet.color}25`,
                borderRadius: '12px',
                color: selectedPlanet.color,
                fontFamily: 'Inter, sans-serif',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${selectedPlanet.color}25`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `${selectedPlanet.color}15`;
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Cosmic Pulse Modal */}
      {showCosmicPulse && (
        <CosmicPulseModal onClose={() => setShowCosmicPulse(false)} />
      )}
    </div>
  );
};

export default ZodiacWheel;
